import { lazy, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Analytics } from "@vercel/analytics/react";

// import "highlight.js/styles/agate.css";
// import "highlight.js/styles/atom-one-dark.css";
import "highlight.js/styles/vs2015.css";
import "katex/dist/katex.min.css";
import "./App.scss";

const Home = lazy(() => import("./pages/Home"));
const DomApi = lazy(() => import("./pages/dom-api/DomApi"));
const GeneralProgramming = lazy(() => import("./pages/GeneralProgramming"));
const React = lazy(() => import("./pages/React"));
const Next = lazy(() => import("./pages/Next"));
const CSS = lazy(() => import("./pages/CSS/CSS"));
const JavaScript = lazy(() => import("./pages/javascript/Javascript"));
const TypeScript = lazy(() => import("./pages/typescript/Typescript"));
const Layout = lazy(() => import("./shared/Layout"));
// data structures
const Array = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Array")
);
const BinaryTree = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/BinaryTree")
);
const Heap = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Heap")
);
const Graph = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Graph")
);
const LinkedList = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/LinkedList")
);
const Map = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Map")
);
const Queue = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Queue")
);
const Set = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Set")
);
const Stack = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Stack")
);
const String = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/String")
);
const Trie = lazy(() =>
  import("./pages/algorithms-data-structures/data-structures/Trie")
);

// algorithms
const SubstringProblemPatterns = lazy(() =>
  import(
    "./pages/algorithms-data-structures/algorithms/SubstringProblemPatterns"
  )
);
const SlidingWindowPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/SlidingWindowPatterns")
);
const TwoPointersPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/TwoPointersPatterns")
);
const DFSBFSPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/DFSBFSPatterns")
);
const BacktrackingPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/BacktrackingPatterns")
);
const DynamicProgramming1D = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/DynamicProgramming1D")
);
const DynamicProgramming2D = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/DynamicProgramming2D")
);
const BinarySearchPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/BinarySearchPatterns")
);
const TreePatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/TreePatterns")
);
const GraphPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/GraphPatterns")
);
const MonotonicStackPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/MonotonicStackPatterns")
);
const StringQuestionPatterns = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/StringQuestionPatterns")
);
const GreedyAlgorithmPatterns = lazy(() =>
  import(
    "./pages/algorithms-data-structures/algorithms/GreedyAlgorithmPatterns"
  )
);
const DSAndAlgos = lazy(() =>
  import("./pages/algorithms-data-structures/DSAndAlgos")
);
const SystemDesignDocs = lazy(() =>
  import("./pages/system-design/SystemDesign")
);
const Requirements = lazy(() => import("./pages/system-design/Requirements"));
const Architecture = lazy(() => import("./pages/system-design/Architecture"));
const WebDev = lazy(() => import("./pages/web-dev/WebDev"));
const A11y = lazy(() => import("./pages/web-dev/A11y"));
const I18n = lazy(() => import("./pages/web-dev/I18n"));
const API = lazy(() => import("./pages/web-dev/API"));
const Caching = lazy(() => import("./pages/web-dev/caching/Caching"));
const Database = lazy(() => import("./pages/web-dev/Database"));
const Embedding = lazy(() => import("./pages/web-dev/Embedding"));
const Devtools = lazy(() => import("./pages/web-dev/Devtools"));
const RenderingApproaches = lazy(() =>
  import("./pages/web-dev/RenderingApproaches")
);
const Principles = lazy(() => import("./pages/web-dev/Principles"));
const Terms = lazy(() => import("./pages/web-dev/Terms"));
const Networking = lazy(() => import("./pages/web-dev/Networking"));
const PerformanceAndMetrics = lazy(() =>
  import("./pages/web-dev/PerformanceAndMetrics")
);
const Security = lazy(() => import("./pages/web-dev/Security"));
const Testing = lazy(() => import("./pages/web-dev/Testing"));
const InterviewPrep = lazy(() =>
  import("./pages/interview-prep/InterviewPrep")
);
const QuestionsForInterviewer = lazy(() =>
  import("./pages/interview-prep/QuestionsForInterviewer")
);
const HandlingCodingExercises = lazy(() =>
  import("./pages/interview-prep/HandlingCodingExercises")
);
const HTML = lazy(() => import("./pages/HTML"));
// project mgmt
const ProjectManagement = lazy(() =>
  import("./pages/project-management/ProjectManagement")
);
const Frameworks = lazy(() => import("./pages/project-management/Frameworks"));
const Methodologies = lazy(() =>
  import("./pages/project-management/Methodologies")
);
const Behavioral = lazy(() => import("./pages/Behavioral"));
const CodingProblems = lazy(() =>
  import("./pages/coding-exercises/CodingExercises")
);
const Chess = lazy(() => import("./pages/coding-exercises/Chess"));
const TipsAndTricks = lazy(() =>
  import("./pages/web-dev/tips-and-tricks/TipsAndTricks")
);
const MasonryLayout = lazy(() =>
  import("./pages/coding-exercises/MasonryLayout")
);
const Progressbar = lazy(() => import("./pages/coding-exercises/ProgressBar"));
const Calculator = lazy(() => import("./pages/coding-exercises/Calculator"));
const RegexPractice = lazy(() =>
  import("./pages/coding-exercises/RegexPractice")
);
const ArrayProblems = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/Array")
);
const PredictiveIndex = lazy(() => import("./pages/PredictiveIndex"));
const FileTree = lazy(() => import("./pages/coding-exercises/Tree"));
const FancySlider = lazy(() => import("./pages/coding-exercises/FancySlider"));
const Interval = lazy(() =>
  import("./pages/algorithms-data-structures/algorithms/Interval")
);

function App() {
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem("themeMode");
    document.body.classList.toggle("dark", savedMode === "dark");
    return savedMode ? savedMode : "light";
  });

  const toggleThemeMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      localStorage.setItem("themeMode", newMode);
      document.body.classList.toggle("dark", newMode === "dark");
      return newMode;
    });
  };

  const theme = createTheme({
    palette: {
      mode: mode,
    },
    // update tabs root background color
    components: {
      MuiTabs: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "dark" ? "#333" : "#f4f4f4",
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          gutterBottom: {
            marginBottom: 14,
          },
        },
      },
      // List item too much spacing
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: 0,
            paddingBottom: 0,
            margin: 0,
          },
        },
      },
    },
    typography: {
      h1: {
        fontSize: "2rem",
      },
      h2: {
        fontSize: "1.75rem",
      },
      h3: {
        fontSize: "1.5rem",
        fontWeight: "bold",
      },
      h4: {
        fontSize: "1.25rem",
        fontWeight: "bold",
      },
      h5: {
        fontSize: "1rem",
        fontWeight: "bold",
      },
      h6: {
        fontSize: "0.875rem",
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* doesnt run on localhost */}
      <Analytics />
      <Router>
        <Routes>
          <Route
            path="/"
            element={<Layout toggleThemeMode={toggleThemeMode} />}
          >
            <Route index element={<Home />} />
            {/* Data Structures */}
            <Route path="data-structures/string" element={<String />} />
            <Route path="data-structures/array" element={<Array />} />
            <Route
              path="data-structures/binary-tree"
              element={<BinaryTree />}
            />
            <Route path="data-structures/graph" element={<Graph />} />
            <Route path="data-structures/heap" element={<Heap />} />
            <Route
              path="data-structures/linked-list"
              element={<LinkedList />}
            />
            <Route path="data-structures/map" element={<Map />} />
            <Route path="data-structures/queue" element={<Queue />} />
            <Route path="data-structures/set" element={<Set />} />
            <Route path="data-structures/stack" element={<Stack />} />
            <Route path="data-structures/trie" element={<Trie />} />

            {/* Algorithms and Data Structures */}
            <Route path="algorithms-data-structures" element={<DSAndAlgos />} />
            {/* Algorithms */}
            <Route
              path="algorithms/substring-problem-patterns"
              element={<SubstringProblemPatterns />}
            />
            <Route
              path="algorithms/sliding-window-patterns"
              element={<SlidingWindowPatterns />}
            />
            <Route
              path="algorithms/two-pointers-patterns"
              element={<TwoPointersPatterns />}
            />
            <Route
              path="algorithms/dfs-bfs-patterns"
              element={<DFSBFSPatterns />}
            />
            <Route
              path="algorithms/backtracking-patterns"
              element={<BacktrackingPatterns />}
            />
            <Route
              path="algorithms/dynamic-programming-1d"
              element={<DynamicProgramming1D />}
            />
            <Route
              path="algorithms/dynamic-programming-2d"
              element={<DynamicProgramming2D />}
            />
            <Route
              path="algorithms/binary-search-patterns"
              element={<BinarySearchPatterns />}
            />
            <Route path="algorithms/tree-patterns" element={<TreePatterns />} />
            <Route
              path="algorithms/graph-patterns"
              element={<GraphPatterns />}
            />
            <Route
              path="algorithms/monotonic-stack-patterns"
              element={<MonotonicStackPatterns />}
            />
            <Route
              path="algorithms/string-question-patterns"
              element={<StringQuestionPatterns />}
            />
            <Route
              path="algorithms/greedy-algorithm-patterns"
              element={<GreedyAlgorithmPatterns />}
            />
            <Route
              path="algorithms/array-patterns"
              element={<ArrayProblems />}
            />
            <Route path="algorithms/interval" element={<Interval />} />

            {/* web dev */}
            <Route path="web-dev" element={<WebDev />} />
            <Route path="web-dev/a11y" element={<A11y />} />
            <Route path="web-dev/i18n" element={<I18n />} />
            <Route path="web-dev/api" element={<API />} />
            <Route path="web-dev/caching" element={<Caching />} />
            <Route path="web-dev/database" element={<Database />} />
            <Route path="web-dev/embedding" element={<Embedding />} />
            <Route path="web-dev/dev-tools" element={<Devtools />} />
            <Route
              path="web-dev/rendering-approaches"
              element={<RenderingApproaches />}
            />
            <Route path="web-dev/principles" element={<Principles />} />
            <Route path="web-dev/terms-and-concepts" element={<Terms />} />
            <Route path="web-dev/networking" element={<Networking />} />
            <Route
              path="web-dev/performance-and-metrics"
              element={<PerformanceAndMetrics />}
            />
            <Route path="web-dev/security" element={<Security />} />
            <Route path="web-dev/testing" element={<Testing />} />
            <Route path="web-dev/tips-and-tricks" element={<TipsAndTricks />} />

            {/* coding problems */}
            <Route path="coding-exercises" element={<CodingProblems />} />
            <Route
              path="coding-exercises/masonry-layout"
              element={<MasonryLayout />}
            />
            <Route
              path="coding-exercises/regex-practice"
              element={<RegexPractice />}
            />
            <Route path="coding-exercises/chess" element={<Chess />} />
            <Route
              path="coding-exercises/progress-bar"
              element={<Progressbar />}
            />
            <Route
              path="coding-exercises/calculator"
              element={<Calculator />}
            />
            <Route path="coding-exercises/file-tree" element={<FileTree />} />
            <Route
              path="coding-exercises/fancy-slider"
              element={<FancySlider />}
            />

            {/* other */}
            <Route path="dom-api" element={<DomApi />} />
            <Route path="css" element={<CSS />} />
            <Route path="javascript" element={<JavaScript />} />
            <Route path="typescript" element={<TypeScript />} />
            <Route path="react" element={<React />} />
            <Route path="next" element={<Next />} />
            <Route
              path="general-programming"
              element={<GeneralProgramming />}
            />
            <Route path="behavioral" element={<Behavioral />} />
            <Route path="interview-prep" element={<InterviewPrep />} />
            <Route
              path="interview-prep/questions-for-interviewer"
              element={<QuestionsForInterviewer />}
            />
            <Route
              path="interview-prep/handling-coding-exercises"
              element={<HandlingCodingExercises />}
            />

            <Route path="html" element={<HTML />} />
            <Route path="predictive-index" element={<PredictiveIndex />} />

            {/* system design */}
            <Route
              path="frontend-system-design"
              element={<SystemDesignDocs />}
            />
            <Route
              path="frontend-system-design/requirements"
              element={<Requirements />}
            />
            <Route
              path="frontend-system-design/architecture"
              element={<Architecture />}
            />

            {/* project management */}
            <Route path="project-management" element={<ProjectManagement />} />
            {/* <Route
              path="project-management/initiation"
              element={<Initiation />}
            /> */}
            {/* <Route path="project-management/planning" element={<Planning />} />
            <Route
              path="project-management/execution-and-control"
              element={<ExecutionAndControl />}
            />
            <Route
              path="project-management/monitoring-and-evaluation"
              element={<MonitoringAndEvaluation />}
            /> */}
            <Route
              path="project-management/methodologies"
              element={<Methodologies />}
            />
            <Route
              path="project-management/frameworks"
              element={<Frameworks />}
            />

            <Route path="*" element={<Navigate to="/" />} />
          </Route>
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
